import { Box, Group, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Space } from '@/api/generated/models';
import type { SpaceNode } from '@/features/price-lists/spaceTreeUtils';
import { IconFigmaChevronDown, IconFigmaChevronRight } from '@/assets/icons';
import { buildTree } from '@/features/price-lists/spaceTreeUtils';

interface SpaceTreeProps {
  objectName: string;
  spaces: Space[];
  onAddChild: (parentSpaceId: string | null) => void;
  onDelete: (spaceId: string) => void;
  onEdit: (space: Space) => void;
  /** Objekt je automatski kloniran iz ugovora druge organizacije (vidi
   * PhysicalObject.clonedFromOrgId) — prostori se samo gledaju, ne uređuju. */
  readOnly?: boolean;
}

const ROW_INDENT = 24;

export const SpaceTree = ({
  objectName,
  spaces,
  onAddChild,
  onDelete,
  onEdit,
  readOnly = false
}: SpaceTreeProps) => {
  const { t } = useTranslation();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const tree = useMemo(() => buildTree(spaces), [spaces]);

  const toggle = (id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (node: SpaceNode, depth: number): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const collapsed = node.id ? collapsedIds.has(node.id) : false;

    return (
      <Box key={node.id}>
        <Group
          gap={8}
          wrap="nowrap"
          py={8}
          pr={20}
          style={{
            paddingLeft: 20 + depth * ROW_INDENT,
            borderTop: '1px solid var(--color-border-soft)'
          }}
        >
          <Box
            role={hasChildren ? 'button' : undefined}
            tabIndex={hasChildren ? 0 : undefined}
            onClick={() => hasChildren && node.id && toggle(node.id)}
            onKeyDown={(event) => {
              if (hasChildren && node.id && (event.key === 'Enter' || event.key === ' '))
                toggle(node.id);
            }}
            style={{
              width: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: hasChildren ? 'pointer' : 'default',
              color: 'var(--color-text-muted)'
            }}
          >
            {hasChildren &&
              (collapsed ? (
                <IconFigmaChevronRight size={12} />
              ) : (
                <IconFigmaChevronDown size={12} />
              ))}
          </Box>

          <Text
            fz={13.5}
            role={readOnly ? undefined : 'button'}
            tabIndex={readOnly ? undefined : 0}
            title={readOnly ? undefined : t('spaceTree.editAction')}
            onClick={readOnly ? undefined : () => onEdit(node)}
            onKeyDown={
              readOnly
                ? undefined
                : (event) => {
                    if (event.key === 'Enter' || event.key === ' ') onEdit(node);
                  }
            }
            style={{
              color: 'var(--color-text-primary)',
              cursor: readOnly ? 'default' : 'pointer'
            }}
            truncate
          >
            {node.name}
            {node.code && (
              <Text span fz={12} style={{ color: 'var(--color-text-muted)' }}>
                {' '}
                ({node.code})
              </Text>
            )}
          </Text>

          {!readOnly && (
            <>
              <Text
                fz={14}
                fw={700}
                role="button"
                tabIndex={0}
                title={t('spaceTree.addChild')}
                onClick={() => node.id && onAddChild(node.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') node.id && onAddChild(node.id);
                }}
                style={{
                  color: 'var(--color-accent-primary-active)',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >
                +
              </Text>

              <Text
                fz={14}
                fw={700}
                role="button"
                tabIndex={0}
                title={t('spaceTree.deleteAction')}
                onClick={() => node.id && onDelete(node.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') node.id && onDelete(node.id);
                }}
                style={{ color: 'var(--color-status-danger)', cursor: 'pointer', lineHeight: 1 }}
              >
                −
              </Text>
            </>
          )}
        </Group>

        {!collapsed && node.children.map((child) => renderNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <Box
      style={{
        borderRadius: 12,
        border: '1px solid var(--color-border-default)',
        backgroundColor: 'var(--color-bg-surface)',
        overflow: 'hidden'
      }}
    >
      <Group py={10} px={20} style={{ backgroundColor: 'var(--color-bg-canvas)' }}>
        <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {objectName}
        </Text>
      </Group>

      {tree.map((node) => renderNode(node, 0))}

      {tree.length === 0 && (
        <Text p={20} fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('spaceTree.emptyState')}
        </Text>
      )}
    </Box>
  );
};
